import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@crowdex/ui/components/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Textarea } from '@crowdex/ui/components/textarea';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight, Plus, X } from 'lucide-react';
import { type ReactNode, useEffect, useEffectEvent, useState } from 'react';
import type { useEnvironmentSettings } from '@/hooks/use-environment-settings';
import type { useWorktreeSettings } from '@/hooks/use-worktree-settings';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import {
  type NativeWorkspaceAction,
  type NativeWorkspaceEnvironment,
  writeNativeWorkspaceEnvironment,
} from '@/lib/native-desktop';
import { SHEET_ON_PHONE } from './dialog-sheet';

type EnvironmentDialogProps = {
  environment: ReturnType<typeof useEnvironmentSettings>;
  worktreeSettings: ReturnType<typeof useWorktreeSettings>;
  workspace: string | null;
  worktreeSourceRoot: string | null;
  workspaceEnvironment: NativeWorkspaceEnvironment | null;
  /** Writes the environment of a repository: the shell keeps it while that one is shown. */
  setWorkspaceEnvironment: (root: string, environment: NativeWorkspaceEnvironment | null) => void;
  preview: boolean;
};

export type EnvironmentVariable = { name: string; value: string };

/** `NAME=value` lines as rows; comments and blank lines are left out. */
function parseEnvironmentText(text: string): EnvironmentVariable[] {
  return text.split('\n').flatMap((line) => {
    const trimmed = line.trim();
    if (trimmed === '' || trimmed.startsWith('#')) return [];
    const equals = line.indexOf('=');
    return equals < 0
      ? [{ name: line.trim(), value: '' }]
      : [{ name: line.slice(0, equals).trim(), value: line.slice(equals + 1) }];
  });
}

/** Rows back to `NAME=value` lines. */
function formatEnvironmentText(variables: readonly EnvironmentVariable[]): string {
  return variables.map((variable) => `${variable.name}=${variable.value}`).join('\n');
}

/** A variable that holds a secret: its value is masked (`DATABASE_URL`, `STRIPE_KEY`). */
function secretVariable(name: string): boolean {
  return /KEY|SECRET|TOKEN|PASSWORD|PASS|URL|DSN|CREDENTIAL/i.test(name);
}

function Section({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-2">
      <h3 className="type-meta text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The repository's setup (design D47, mobile 44): its setup script, its variables (one row
 * each, secrets masked), and the repository actions the conversation header shows, as chips. The
 * cleanup script, platform scripts and worktree preferences are under More settings. Saved in the
 * repository for the team.
 */
export function EnvironmentDialog({
  environment,
  worktreeSettings,
  workspace,
  worktreeSourceRoot,
  workspaceEnvironment,
  setWorkspaceEnvironment,
  preview,
}: EnvironmentDialogProps) {
  const {
    environmentDialogOpen,
    setEnvironmentDialogOpen,
    setEnvironmentError,
    setEnvironmentProjectRoot,
    environmentDraft,
    setEnvironmentDraft,
    environmentEnvText,
    setEnvironmentEnvText,
    environmentError,
    environmentProjectRoot,
    saveEnvironment,
  } = environment;
  const {
    worktreeRoot,
    setWorktreeRoot,
    worktreeAutoCleanup,
    setWorktreeAutoCleanup,
    worktreeKeepCount,
    setWorktreeKeepCount,
  } = worktreeSettings;
  const [actionId, setActionId] = useState<string | null>(null);
  const root = environmentProjectRoot ?? worktreeSourceRoot ?? workspace;
  const repository = useWorkspaceName(root);
  // Rows while the dialog is open (a new one starts empty); the text keeps the named ones.
  const [variables, setRows] = useState<EnvironmentVariable[]>([]);
  // Read once as it opens: the rows are the source while it is open.
  const readRows = useEffectEvent(() => setRows(parseEnvironmentText(environmentEnvText)));
  useEffect(() => {
    if (environmentDialogOpen) readRows();
  }, [environmentDialogOpen]);
  const setVariables = (next: EnvironmentVariable[]) => {
    setRows(next);
    setEnvironmentEnvText(
      formatEnvironmentText(next.filter((variable) => variable.name.trim() !== '')),
    );
  };
  const actions = environmentDraft.actions ?? [];
  const editing = actions.find((action) => action.id === actionId);
  const updateAction = (id: string, change: Partial<NativeWorkspaceAction>) =>
    setEnvironmentDraft((current) => ({
      ...current,
      actions: current.actions?.map((candidate) =>
        candidate.id === id ? { ...candidate, ...change } : candidate,
      ),
    }));

  return (
    <Dialog
      onOpenChange={(open) => {
        setEnvironmentDialogOpen(open);
        if (!open) {
          setEnvironmentError(null);
          setEnvironmentProjectRoot(null);
          setActionId(null);
        }
      }}
      open={environmentDialogOpen}
    >
      <DialogContent
        className={cn('max-h-[88vh] gap-4 overflow-y-auto sm:max-w-2xl', SHEET_ON_PHONE)}
        data-environment-dialog
      >
        <DialogHeader>
          <DialogTitle className="type-card-title">
            Repository setup{repository === null ? '' : ` · ${repository}`}
          </DialogTitle>
          <DialogDescription>Applies to every conversation in this repository</DialogDescription>
        </DialogHeader>
        <Section title="Setup script">
          <Textarea
            aria-label="Setup script"
            className="min-h-24 resize-y rounded-xl border-0 bg-code-surface font-mono text-sm leading-6"
            id="worktree-setup-script"
            onChange={(event) =>
              setEnvironmentDraft((current) => ({ ...current, setupScript: event.target.value }))
            }
            placeholder="pnpm install"
            value={environmentDraft.setupScript}
          />
        </Section>
        <Section title={`Environment variables · ${variables.length}`}>
          <div className="grid gap-2">
            {variables.map((variable, index) => (
              <div
                className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
                // biome-ignore lint/suspicious/noArrayIndexKey: Rows are edited in place: their position is their identity.
                key={index}
              >
                <Input
                  aria-label="Variable name"
                  className="h-control-md rounded-lg bg-control font-mono text-xs"
                  onChange={(event) =>
                    setVariables(
                      variables.map((entry, position) =>
                        position === index ? { ...entry, name: event.target.value } : entry,
                      ),
                    )
                  }
                  value={variable.name}
                />
                <Input
                  aria-label={`Value of ${variable.name}`}
                  className="h-control-md rounded-lg bg-control font-mono text-xs"
                  onChange={(event) =>
                    setVariables(
                      variables.map((entry, position) =>
                        position === index ? { ...entry, value: event.target.value } : entry,
                      ),
                    )
                  }
                  type={secretVariable(variable.name) ? 'password' : 'text'}
                  value={variable.value}
                />
                <Button
                  aria-label={`Remove ${variable.name}`}
                  onClick={() =>
                    setVariables(variables.filter((_entry, position) => position !== index))
                  }
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            ))}
            <button
              className="type-meta justify-self-start text-foreground hover:underline"
              onClick={() => setVariables([...variables, { name: '', value: '' }])}
              type="button"
            >
              + Add a variable
            </button>
          </div>
        </Section>
        <Section title="Repository actions · shown in the conversation header">
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => (
              <button
                aria-pressed={actionId === action.id}
                className={cn(
                  'h-8 rounded-control px-3.5 font-mono text-sm transition-colors',
                  actionId === action.id ? 'bg-foreground/15 text-foreground' : 'bg-control',
                )}
                key={action.id}
                onClick={() => setActionId(actionId === action.id ? null : action.id)}
                type="button"
              >
                {action.name || 'Untitled'}
              </button>
            ))}
            <button
              className="h-8 rounded-control border border-hairline border-dashed px-3.5 text-muted-foreground text-sm hover:text-foreground"
              onClick={() => {
                const id = crypto.randomUUID();
                setEnvironmentDraft((current) => ({
                  ...current,
                  actions: [
                    ...(current.actions ?? []),
                    { id, name: 'Run', icon: 'play', script: '', scripts: {} },
                  ],
                }));
                setActionId(id);
              }}
              type="button"
            >
              <Plus aria-hidden className="mr-1 inline size-3.5" />
              Add
            </button>
          </div>
          {editing !== undefined && (
            <div className="grid gap-2 rounded-xl bg-control/50 p-3" data-action-editor>
              <div className="flex items-center gap-2">
                <Select
                  onValueChange={(value) =>
                    updateAction(editing.id, { icon: value as NativeWorkspaceAction['icon'] })
                  }
                  value={editing.icon}
                >
                  <SelectTrigger
                    aria-label={`Icon for ${editing.name}`}
                    className="h-control-md w-32"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="play">Run</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="build">Build</SelectItem>
                    <SelectItem value="terminal">Terminal</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  aria-label="Action name"
                  className="h-control-md flex-1"
                  onChange={(event) => updateAction(editing.id, { name: event.target.value })}
                  placeholder="Run"
                  value={editing.name}
                />
                <Button
                  aria-label={`Remove ${editing.name}`}
                  onClick={() => {
                    setEnvironmentDraft((current) => ({
                      ...current,
                      actions: current.actions?.filter((candidate) => candidate.id !== editing.id),
                    }));
                    setActionId(null);
                  }}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
              <Textarea
                aria-label={`Default script for ${editing.name}`}
                className="min-h-16 resize-y rounded-lg font-mono text-xs"
                onChange={(event) => updateAction(editing.id, { script: event.target.value })}
                placeholder="pnpm dev"
                value={editing.script}
              />
              <div className="grid gap-2 md:grid-cols-3">
                {(['macos', 'windows', 'linux'] as const).map((platform) => (
                  <Textarea
                    aria-label={`${platform} script for ${editing.name}`}
                    className="min-h-14 resize-y rounded-lg font-mono text-xs"
                    key={platform}
                    onChange={(event) =>
                      updateAction(editing.id, {
                        scripts: { ...(editing.scripts ?? {}), [platform]: event.target.value },
                      })
                    }
                    placeholder={`${platform} override`}
                    value={editing.scripts?.[platform] ?? ''}
                  />
                ))}
              </div>
            </div>
          )}
        </Section>
        <Collapsible>
          <CollapsibleTrigger className="type-meta group flex items-center gap-1 text-muted-foreground hover:text-foreground">
            <ChevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
            More settings
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 grid gap-3">
            <label className="grid gap-1.5 text-sm" htmlFor="worktree-environment-name">
              <span className="type-meta text-muted-foreground">Name</span>
              <Input
                className="h-control-md rounded-lg"
                id="worktree-environment-name"
                onChange={(event) =>
                  setEnvironmentDraft((current) => ({ ...current, name: event.target.value }))
                }
                value={environmentDraft.name}
              />
            </label>
            <label className="grid gap-1.5 text-sm" htmlFor="worktree-cleanup-script">
              <span className="type-meta text-muted-foreground">Cleanup script</span>
              <Textarea
                className="min-h-16 resize-y rounded-lg font-mono text-xs"
                id="worktree-cleanup-script"
                onChange={(event) =>
                  setEnvironmentDraft((current) => ({
                    ...current,
                    cleanupScript: event.target.value,
                  }))
                }
                placeholder="pnpm run cleanup"
                value={environmentDraft.cleanupScript}
              />
            </label>
            <div className="grid gap-1.5">
              <span className="type-meta text-muted-foreground">
                Platform setup scripts · override the setup script on that system
              </span>
              <div className="grid gap-2 md:grid-cols-3">
                {(['macos', 'windows', 'linux'] as const).map((platform) => (
                  <Textarea
                    aria-label={`${platform} setup script`}
                    className="min-h-16 resize-y rounded-lg font-mono text-xs"
                    key={platform}
                    onChange={(event) =>
                      setEnvironmentDraft((current) => ({
                        ...current,
                        setupScripts: {
                          ...(current.setupScripts ?? {}),
                          [platform]: event.target.value,
                        },
                      }))
                    }
                    placeholder={platform}
                    value={environmentDraft.setupScripts?.[platform] ?? ''}
                  />
                ))}
              </div>
            </div>
            <label className="grid gap-1.5 text-sm" htmlFor="worktree-root">
              <span className="type-meta text-muted-foreground">Worktree root</span>
              <Input
                className="h-control-md rounded-lg font-mono text-xs"
                id="worktree-root"
                onChange={(event) => setWorktreeRoot(event.target.value)}
                placeholder="~/.crowdex/worktrees (default)"
                value={worktreeRoot}
              />
            </label>
            <div className="grid gap-2">
              <label className="flex items-center gap-2 text-sm" htmlFor="worktree-auto-cleanup">
                <Checkbox
                  checked={worktreeAutoCleanup}
                  id="worktree-auto-cleanup"
                  onCheckedChange={(value) => setWorktreeAutoCleanup(value === true)}
                />
                Automatically delete old managed worktrees
              </label>
              <label
                className="flex items-center justify-between gap-3 text-muted-foreground text-sm"
                htmlFor="worktree-keep-count"
              >
                <span>Worktrees to keep</span>
                <Input
                  className="h-control-sm w-20"
                  disabled={!worktreeAutoCleanup}
                  id="worktree-keep-count"
                  min={1}
                  onChange={(event) =>
                    setWorktreeKeepCount(Math.max(1, Number.parseInt(event.target.value, 10) || 1))
                  }
                  type="number"
                  value={worktreeKeepCount}
                />
              </label>
              <p className="type-meta text-muted-foreground">
                The current conversation and permanent worktrees are protected. Deleted managed
                worktrees keep a restorable snapshot.
              </p>
            </div>
            {workspaceEnvironment !== null && (
              <Button
                className="justify-self-start"
                onClick={() => {
                  if (root === null || preview) return;
                  void writeNativeWorkspaceEnvironment(root, null).then(() => {
                    setWorkspaceEnvironment(root, null);
                    setEnvironmentDialogOpen(false);
                  });
                }}
                variant="ghost"
              >
                Work without environment
              </Button>
            )}
          </CollapsibleContent>
        </Collapsible>
        {environmentError !== null && (
          <p className="text-destructive text-sm">{environmentError}</p>
        )}
        <DialogFooter className="items-center gap-2 border-hairline border-t pt-4 max-md:flex-row">
          <p className="type-meta mr-auto text-muted-foreground max-md:hidden">
            Saved in the repository for your team
          </p>
          <Button
            className="max-md:hidden"
            onClick={() => setEnvironmentDialogOpen(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="rounded-control max-md:h-12 max-md:flex-1"
            disabled={environmentDraft.name.trim().length === 0 || preview}
            onClick={() => void saveEnvironment()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
